import React, { useState, useRef, useCallback } from 'react';
import { Card, Button, Nav, Toast, ToastContainer } from 'react-bootstrap';
import Cart from '../Cart';
import AddressSelector, { AddressSelectorRef } from './AddressSelector';
import PaymentMethodSelector from './PaymentMethodSelector';
import './CartStepper.css';
import { useCartStepper } from '../hooks/use-cart-stepper';
import { useUserStore } from '../../../store/user-store';
import ValidationModalForm from './ValidationModal';
import CarrierSelector from './CarrierSelector';

interface CartStepperProps {
  initialStep?: number;
}

const CartStepper: React.FC<CartStepperProps> = ({ initialStep = 0 }) => {
  // Usamos el hook personalizado para manejar toda la lógica del stepper
  const {
    activeStep,
    checkoutData,
    showSuccessToast,
    orderResponse,
    isEmpty,
    items,
    steps,
    destination,
    error,
    setError,
    updateCheckoutData,
    handleNext,
    handlePrevious,
    handleOrderSuccess,
    handleOrderError,
    setShowSuccessToast,
    setActiveStep,
    setDestination,
    carrierBlocked,
  } = useCartStepper({ initialStep });

  const user = useUserStore((state) => state.user);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const addressSelectorRef = useRef<AddressSelectorRef>(null);

  const handleAddressSelect = useCallback((addressId: string | null, shippingInfo: any) => {
    console.log('Address selected:', addressId, shippingInfo);
    updateCheckoutData({ addressId, shippingInfo });
  }, [updateCheckoutData]);

  const handleSelectDeliveryType = useCallback((deliveryType: 'DELIVERY' | 'STORE_PICKUP') => {
    console.log('Delivery type selected:', deliveryType);
    updateCheckoutData({ deliveryType });
  }, [updateCheckoutData]);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <Cart onProceedToCheckout={handleNext} />;
      case 1:
        return <AddressSelector
          ref={addressSelectorRef}
          onAddressSelect={handleAddressSelect}
          onSelectDeliveryType={handleSelectDeliveryType}
          selectedAddressId={checkoutData.addressId}
          shippingInfo={checkoutData.shippingInfo}
          deliveryType={checkoutData.deliveryType}
          onConfirmAndContinue={() => {
            const needsValidation = validateUserFields();
            if (needsValidation) {
              setShowValidationModal(true);
              return;
            }
            handleNext();
          }}
          setDestination={setDestination}
        />;
      case 2:
        return <CarrierSelector unavailable={carrierBlocked} checkoutData={checkoutData!} destination={destination} cartItems={items} selectCarrier={(data) => updateCheckoutData({ carrierId: data.carrierId, shippingCost: data.shippingCost })} />;
      case 3:
        return <PaymentMethodSelector
          checkoutData={checkoutData!}
          cartItems={items}
          onPaymentMethodSelect={(method) => updateCheckoutData({ paymentMethod: method })}
          onOrderSuccess={handleOrderSuccess}
          onOrderError={handleOrderError}
        />;
      default:
        return <Cart onProceedToCheckout={handleNext} />;
    }
  };

  if (isEmpty) {
    return <Cart onProceedToCheckout={handleNext} />;
  }

  const validateUserFields = () => {
    if (!user) return true;
    console.log("Validating user fields:", user);
    if (!user.documentType) {
      return true;
    }

    if (!user.document) {
      return true;
    }

    if (!user.cellphone) {
      return true;
    }

    return false;
  };




  return (
    <>
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1 }}>
        <Toast
          show={showSuccessToast}
          onClose={() => setShowSuccessToast(false)}
          delay={5000}
          autohide
          bg="success"
        >
          <Toast.Header>
            <strong className="me-auto">¡Pedido realizado!</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            Tu pedido ha sido procesado correctamente.
            {orderResponse && <div>Orden #{orderResponse.order?._id?.substring(0, 8)}</div>}
          </Toast.Body>
        </Toast>
        <Toast
          show={Boolean(error)}
          onClose={() => setError(null)}
          delay={5000}
          autohide
          bg="warning"
        >
          <Toast.Header>
            <strong className="me-auto">Verifique los datos</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            {error}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <Card className="bg-white border-0 rounded-3 mb-4">
        <Card.Body className="p-4">
          <Nav className="stepper-nav mb-4">
            {steps.map((step, index) => (
              <Nav.Item key={step.key} className="stepper-item">
                <div
                  className={`stepper-circle ${index === activeStep ? 'active' : ''} ${index < activeStep ? 'completed' : ''}`}
                  onClick={() => index < activeStep && setActiveStep(index)}
                >
                  {index < activeStep ? (
                    <i className="material-symbols-outlined">check</i>
                  ) : (
                    <i className="material-symbols-outlined">{step.icon}</i>
                  )}
                </div>
                <div className="stepper-label">{step.title}</div>
                {index < steps.length - 1 && <div className="stepper-line"></div>}
              </Nav.Item>
            ))}
          </Nav>

          <div className="step-content">
            {renderStepContent()}
          </div>

          <div className="d-flex justify-content-between mt-4">
            {activeStep > 0 && (
              <Button variant="outline-secondary" onClick={handlePrevious}>
                <i className="ri-arrow-left-line me-2"></i>
                Regresar
              </Button>
            )}

            {activeStep < steps.length - 1 && (
              <div className="ms-auto text-end">
                <Button
                  variant="primary"
                  onClick={() => {
                    if (activeStep === 1 && (checkoutData.deliveryType === 'STORE_PICKUP' || checkoutData.addressId || checkoutData.shippingInfo?.country_id)) {
                      // Mostrar modal de confirmación de dirección
                      addressSelectorRef.current?.showConfirmation();
                      return;
                    }
                    handleNext();
                  }}
                  disabled={isEmpty || (activeStep === 2 && carrierBlocked)}
                >
                  Continuar
                  <i className="ri-arrow-right-line ms-2"></i>
                </Button>
              </div>
            )}


          </div>
        </Card.Body>
      </Card>
      {/* Modal de validación */}
      {showValidationModal && (
        <ValidationModalForm
          show={showValidationModal}
          onClose={() => setShowValidationModal(false)}
          handleNext={handleNext}
        />)}

    </>
  );
};

export default CartStepper;
